import { waitForContentShown } from './contentShownSignal';
import { logScenarioStart, logScenarioStop } from './scenarioTelemetry';
import type { ScenarioId, ScenarioSource, ScenarioStatus } from './types';

/**
 * Interaction scenarios complete in seconds, so they use a far shorter window than the
 * facade's 5-minute default. A submit whose content never paints within this window is
 * reported as `timeout` by the facade.
 */
const defaultTimeoutInMS = 30_000;

export interface StartPostShownScenarioOptions {
  readonly source?: ScenarioSource;
  readonly timeoutInMS?: number;
  /**
   * Optional observability hook fired once with the terminal status when the handle settles
   * the scenario (`ok`/`failure`/`cancel`). The reported telemetry is unchanged; this exists
   * so the app layer can surface the outcome (e.g. a dev console line) without the lean
   * telemetry module taking a logging dependency. It is not fired for `timeout`, which the
   * façade reports independently via the performance measure's auto-timeout.
   */
  readonly onSettled?: (status: ScenarioStatus) => void;
}

export interface PostShownScenarioHandle {
  /**
   * The scenario's opaque id. Exposed so a seam adapter can attribute backend calls to this
   * scenario in the traceability call-ledger (`recordScenarioCall`).
   */
  readonly scenarioId: ScenarioId;
  /**
   * Call when the publish is server-confirmed, passing the identifier the rendered feed node
   * will report (the thread's `telemetryId`). Stops the scenario as `ok` once that node paints.
   */
  readonly publishSucceeded: (contentKey: string) => void;
  /** Call when the publish request fails. Stops the scenario as `failure`. */
  readonly publishFailed: (error?: Error) => void;
  /** Call when the user abandons the compose before it completes. Stops the scenario as `cancel`. */
  readonly abandon: () => void;
}

/**
 * Starts a `post_shown` scenario at submit time and returns a handle to drive it from the
 * publish lifecycle. The scenario stops as `ok` only when the composed post's server-confirmed
 * node actually paints in the feed (via the content-shown signal), giving a true submit -> shown
 * latency and reliability signal rather than a submit -> server-ack one.
 *
 * Data-layer agnostic: the caller decides where the lifecycle signals come from. On web's
 * primary feed that is the Redux `publishGroupMessage` thunk
 * (`PENDING` -> start, `FULFILLED` -> `publishSucceeded`, `REJECTED` -> `publishFailed`); a
 * Relay publisher would drive the same handle from its mutation callbacks.
 */
export const startPostShownScenario = ({
  source,
  timeoutInMS = defaultTimeoutInMS,
  onSettled,
}: StartPostShownScenarioOptions = {}): PostShownScenarioHandle => {
  const scenarioId = logScenarioStart({ name: 'post_shown', source, timeoutInMS });

  let settled = false;
  let stopWaiting: (() => void) | undefined;
  let guardTimer: number | undefined;

  const cleanup = () => {
    if (guardTimer !== undefined) {
      window.clearTimeout(guardTimer);
      guardTimer = undefined;
    }
    stopWaiting?.();
    stopWaiting = undefined;
  };

  const stop = (status: ScenarioStatus, error?: Error) => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    logScenarioStop({ scenarioId, status, error });
    onSettled?.(status);
  };

  return {
    scenarioId,
    publishSucceeded: (contentKey: string) => {
      if (settled || stopWaiting) {
        return;
      }

      // The facade reports `timeout` if the node never paints; this guard independently stops
      // waiting so a never-shown key can't leak a waiter past the scenario's lifetime.
      guardTimer = window.setTimeout(cleanup, timeoutInMS);

      stopWaiting = waitForContentShown(contentKey, () => {
        stop('ok');
      });
    },
    publishFailed: (error?: Error) => {
      stop('failure', error);
    },
    abandon: () => {
      stop('cancel');
    },
  };
};
